import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { SafeDatabaseUser } from '../../../types/types';
import { reportUser } from '../../../services/userService';
import useToast from '../../../hooks/useToast';

interface ReportUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportedUser: SafeDatabaseUser | null;
  currentUser: SafeDatabaseUser | null;
}

/**
 * Report User Modal Component
 *
 * Provides a dialog for reporting a user for inappropriate behavior.
 * Users can enter a reason for the report and submit it for admin review.
 */
const ReportUserModal: React.FC<ReportUserModalProps> = ({
  isOpen,
  onClose,
  reportedUser,
  currentUser,
}) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  if (!reportedUser || !currentUser) {
    return null;
  }

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast({
        title: 'Error',
        description: 'Please provide a reason for the report',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await reportUser(currentUser.username, reportedUser.username, reason);

      if (response.error) {
        throw new Error(response.error as string);
      }

      toast({
        title: 'Report Submitted',
        description: 'Your report has been submitted and will be reviewed by administrators.',
      });

      setReason('');
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to submit report: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Report User</DialogTitle>
          <DialogDescription>
            Report {reportedUser.username} for inappropriate behavior. Your report will be reviewed
            by administrators.
          </DialogDescription>
        </DialogHeader>
        <div className='grid gap-4 py-4'>
          <div className='space-y-2'>
            <label htmlFor='reason' className='text-sm font-medium'>
              Reason for Report
            </label>
            <Textarea
              id='reason'
              placeholder='Please explain why you are reporting this user'
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={5}
              className='resize-none'
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReportUserModal;
